import React, { useState } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { MessageCircle, AlertCircle, Copy } from 'lucide-react';

/**
 * Format any phone string into Algerian international format (213XXXXXXXXX)
 * - removes all non-digit characters
 * - converts leading 0 → 213
 * - if already starts with 213 → keep
 * - otherwise → prepend 213 (assumes Algerian)
 */
export const formatPhoneForWhatsApp = (rawPhone) => {
  if (!rawPhone) return null;
  const digits = String(rawPhone).replace(/\D/g, '');
  if (!digits) return null;
  if (digits.startsWith('00')) return digits.slice(2);
  if (digits.startsWith('213')) return digits;
  if (digits.startsWith('0')) return '213' + digits.slice(1);
  // if starts with 5,6,7 (Algerian mobile), prepend 213
  if (/^[5-7]/.test(digits)) return '213' + digits;
  return digits;
};

/**
 * Validate phone (Algerian format)
 * Returns { valid: bool, error: string|null }
 */
export const validatePhone = (rawPhone) => {
  const formatted = formatPhoneForWhatsApp(rawPhone);
  if (!formatted) return { valid: false, error: 'لا يوجد رقم هاتف' };
  if (formatted.length < 11) return { valid: false, error: 'رقم الهاتف قصير جداً' };
  if (formatted.length > 15) return { valid: false, error: 'رقم الهاتف طويل جداً' };
  if (!formatted.startsWith('213')) return { valid: false, error: 'الرقم يجب أن يبدأ بكود الجزائر (213)' };
  return { valid: true, error: null, formatted };
};

/**
 * Built-in templates. Use {{var}} placeholders.
 */
export const WA_TEMPLATES = [
  {
    id: 'umrah_offer',
    label: '🕋 عرض عمرة',
    text: `السلام عليكم {{client_name}} 🌙

نتشرف بدعوتكم لاكتشاف عرضنا الجديد:
✨ {{package_name}}
💰 السعر: {{price}} دج
{{description}}

هل تحبون أن أرسل لكم تفاصيل أكثر؟
وكالات صنهاجة للسفر 🕋`
  },
  {
    id: 'reminder',
    label: '⏰ تذكير',
    text: `السلام عليكم {{client_name}}،

نذكّركم بموعد المتابعة المتفق عليه.
يسعدنا التواصل معكم لإكمال {{package_name}}.

نحن في انتظاركم.
وكالات صنهاجة للسفر`
  },
  {
    id: 'follow_up',
    label: '📞 متابعة',
    text: `السلام عليكم {{client_name}} 👋

نتمنى أن تكونوا بخير.
نتابع معكم بخصوص العرض الذي قدمناه لكم.
هل لديكم أسئلة أو ملاحظات؟

نحن جاهزون لخدمتكم.
وكالات صنهاجة للسفر`
  },
  {
    id: 'booking_confirm',
    label: '✅ تأكيد حجز',
    text: `السلام عليكم {{client_name}} 🌙

نؤكد لكم حجزكم بنجاح:
✈️ {{package_name}}
💰 السعر النهائي: {{price}} دج
{{description}}

شكراً لثقتكم بنا 🤝
وكالات صنهاجة للسفر`
  },
  {
    id: 'after_trip',
    label: '🤲 شكر بعد السفر',
    text: `السلام عليكم {{client_name}} 🌙

تقبّل الله منكم صالح الأعمال 🤲
يسعدنا أن نكون قد ساهمنا في إتمام رحلتكم.
نتمنى أن تكون التجربة قد نالت رضاكم.

نشرفكم بزيارتنا في أي وقت.
وكالات صنهاجة للسفر`
  },
  {
    id: 'custom',
    label: '✏️ رسالة مخصصة',
    text: ''
  }
];

/**
 * Replace {{vars}} in template text with values
 */
const renderTemplate = (text, vars) => {
  if (!text) return '';
  let out = text;
  Object.entries(vars || {}).forEach(([k, v]) => {
    const re = new RegExp(`{{\\s*${k}\\s*}}`, 'g');
    out = out.replace(re, v != null && v !== '' ? String(v) : '');
  });
  // Clean lingering placeholders + double newlines
  out = out.replace(/{{\s*\w+\s*}}/g, '').replace(/\n{3,}/g, '\n\n');
  return out;
};

/**
 * Build WhatsApp link — wa.me ONLY (official format).
 * Best practices:
 *   - International phone format (no +, no spaces) — handled by validatePhone
 *   - URL-encoded message
 *   - Returns plain wa.me URL; opening must be done via target="_blank" + noopener,noreferrer
 *
 * NOTE: do NOT use this URL inside an iframe — must be opened in a new tab.
 */
export const buildWhatsAppURL = (phone, message) => {
  const v = validatePhone(phone);
  if (!v.valid) return { url: null, error: v.error };
  const text = encodeURIComponent(message || '');
  return { url: `https://wa.me/${v.formatted}?text=${text}`, error: null };
};

/**
 * Reusable helper as required by the spec:
 *   generateWhatsAppLink(phone, message) → string|null
 * Returns the wa.me URL (or null if phone invalid).
 */
export const generateWhatsAppLink = (phone, message) => {
  const { url } = buildWhatsAppURL(phone, message);
  return url;
};

/**
 * Click tracking helper (console + window.gtag if available)
 */
const trackWhatsAppClick = (meta = {}) => {
  try {
    // eslint-disable-next-line no-console
    console.log('[WhatsApp Click]', { timestamp: new Date().toISOString(), ...meta });
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'whatsapp_click', meta);
    }
  } catch (_) { /* ignore */ }
};

/**
 * Quick send helper. Opens the wa.me link in a new tab using window.open ONLY.
 * NO internal redirect (no window.location), NO router navigation, NO iframe.
 * IMPORTANT: must be called directly from a user gesture (click handler).
 *
 * For best reliability prefer the <WhatsAppButton/> component which uses an
 * <a target="_blank" rel="noopener noreferrer"> element directly.
 */
export const quickSendWhatsApp = (phone, message, meta = {}) => {
  const { url, error } = buildWhatsAppURL(phone, message);
  if (error) {
    alert('❌ ' + error);
    return false;
  }
  trackWhatsAppClick({ phone, ...meta });
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
};

/**
 * Reusable WhatsApp button with template selection dialog.
 *
 * props:
 *   phone (string)         — required — client phone
 *   clientName (string)    — required — client name
 *   packageName (string)   — optional — package/service/trip name
 *   price (number|string)  — optional — total price
 *   description (string)   — optional — extra details
 *   variant?: 'default' | 'compact' | 'icon-only'
 *   className?: string
 *   buttonText?: string    — overrides default "WhatsApp"
 */
const WhatsAppButton = ({
  phone,
  clientName = '',
  packageName = '',
  price = '',
  description = '',
  variant = 'default',
  className = '',
  buttonText = 'WhatsApp',
  defaultTemplate = 'follow_up'
}) => {
  const [open, setOpen] = useState(false);
  const [tplId, setTplId] = useState(defaultTemplate);
  const [phoneOverride, setPhoneOverride] = useState('');
  const [customText, setCustomText] = useState('');

  const tpl = WA_TEMPLATES.find(t => t.id === tplId) || WA_TEMPLATES[0];
  const vars = {
    client_name: clientName,
    package_name: packageName,
    price: price ? Number(price).toLocaleString() : '',
    description: description
  };
  const renderedText = tplId === 'custom' ? customText : renderTemplate(tpl.text, vars);

  const effectivePhone = phoneOverride || phone;
  const phoneStatus = validatePhone(effectivePhone);

  // Direct anchor link is used for opening WhatsApp; handleSend kept only for legacy compat
  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(renderedText);
      const v = validatePhone(effectivePhone);
      const phoneInfo = v.valid ? `\n\n📞 الرقم: ${v.formatted}` : '';
      alert('✅ تم نسخ الرسالة!' + phoneInfo + '\n\nالخطوات:\n1. افتح WhatsApp على هاتفك أو الكمبيوتر\n2. ابحث عن الرقم أو افتح المحادثة\n3. الصق الرسالة (Ctrl+V)');
      trackWhatsAppClick({ phone: effectivePhone, template: tplId, clientName, action: 'copy' });
    } catch (e) {
      alert('❌ تعذر النسخ. حاول يدوياً من حقل الرسالة.');
    }
  };

  const handleCopyURL = async () => {
    const { url, error } = buildWhatsAppURL(effectivePhone, renderedText);
    if (error) { alert('❌ ' + error); return; }
    try {
      await navigator.clipboard.writeText(url);
      alert('✅ تم نسخ رابط wa.me!\n\nالصقه في شريط العنوان أو متصفح آخر/شبكة أخرى.');
      trackWhatsAppClick({ phone: effectivePhone, template: tplId, action: 'copy_url' });
    } catch (e) {
      alert('❌ تعذر النسخ.');
    }
  };

  // Pre-compute the wa.me URL for displaying in the dialog
  const previewURL = phoneStatus.valid
    ? `https://wa.me/${phoneStatus.formatted}?text=${encodeURIComponent(renderedText)}`
    : '';

  // Trigger button styles based on variant
  const triggerBtn = variant === 'icon-only' ? (
    <Button
      size="sm"
      variant="outline"
      onClick={() => setOpen(true)}
      className={`bg-green-50 hover:bg-green-100 border-green-300 text-green-700 ${className}`}
      data-testid="whatsapp-trigger-btn"
      title="إرسال WhatsApp"
    >
      <MessageCircle className="h-4 w-4" />
    </Button>
  ) : variant === 'compact' ? (
    <Button
      size="sm"
      variant="outline"
      onClick={() => setOpen(true)}
      className={`bg-green-50 hover:bg-green-100 border-green-300 text-green-700 ${className}`}
      data-testid="whatsapp-trigger-btn"
    >
      <MessageCircle className="h-4 w-4 ml-1" /> {buttonText}
    </Button>
  ) : (
    <Button
      onClick={() => setOpen(true)}
      className={`bg-green-600 hover:bg-green-700 text-white ${className}`}
      data-testid="whatsapp-trigger-btn"
    >
      <MessageCircle className="h-4 w-4 ml-2" /> {buttonText}
    </Button>
  );

  return (
    <>
      {triggerBtn}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-green-600" /> إرسال WhatsApp
            </DialogTitle>
            <DialogDescription>اختر قالب الرسالة، عدّل النص، ثم أرسل عبر WhatsApp Web</DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Phone Override */}
            <div>
              <Label>الرقم</Label>
              <Input
                value={phoneOverride || phone || ''}
                onChange={e => setPhoneOverride(e.target.value)}
                placeholder="0555..."
                data-testid="wa-phone-input"
              />
              {phoneStatus.valid ? (
                <p className="text-xs text-green-700 mt-1">✓ سيُرسل إلى: {phoneStatus.formatted}</p>
              ) : (
                <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> {phoneStatus.error}
                </p>
              )}
            </div>

            {/* Template Selector */}
            <div>
              <Label>القالب</Label>
              <Select value={tplId} onValueChange={setTplId}>
                <SelectTrigger data-testid="wa-template-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WA_TEMPLATES.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Variables hint (only for non-custom) */}
            {tplId !== 'custom' && (
              <div className="bg-brand-50 border border-brand-200 rounded p-2 text-xs text-brand-800">
                <p className="font-medium mb-1">المتغيرات المُستبدَلة:</p>
                <ul className="space-y-0.5">
                  <li>• اسم العميل: <span className="font-mono">{clientName || '—'}</span></li>
                  {packageName && <li>• الباقة/الخدمة: <span className="font-mono">{packageName}</span></li>}
                  {price && <li>• السعر: <span className="font-mono">{Number(price).toLocaleString()} دج</span></li>}
                  {description && <li>• الوصف: <span className="font-mono">{description.substring(0, 40)}...</span></li>}
                </ul>
              </div>
            )}

            {/* Message preview / custom textarea */}
            <div>
              <Label>الرسالة (يمكنك تعديلها)</Label>
              {tplId === 'custom' ? (
                <Textarea
                  value={customText}
                  onChange={e => setCustomText(e.target.value)}
                  rows={8}
                  placeholder="اكتب رسالتك هنا..."
                  data-testid="wa-message-textarea"
                />
              ) : (
                <Textarea
                  value={renderedText}
                  onChange={e => {
                    setTplId('custom');
                    setCustomText(e.target.value);
                  }}
                  rows={8}
                  data-testid="wa-message-textarea"
                />
              )}
              <p className="text-xs text-gray-500 mt-1">{renderedText.length} حرف</p>
            </div>

            {/* Direct wa.me Link Display - the actual URL */}
            {phoneStatus.valid && (
              <div className="bg-green-50 border-2 border-green-300 rounded p-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs text-green-800 font-bold">🔗 رابط wa.me المباشر:</Label>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleCopyURL}
                    className="bg-white hover:bg-green-100 border-green-400 text-green-700 h-7 text-xs"
                    data-testid="wa-copy-url-btn"
                  >
                    <Copy className="h-3 w-3 ml-1" /> نسخ الرابط
                  </Button>
                </div>
                <div className="bg-white border border-green-200 rounded p-2 max-h-20 overflow-auto">
                  <a
                    href={previewURL}
                    target="_blank"
                    rel="noopener noreferrer external"
                    referrerPolicy="no-referrer"
                    onClick={(e) => {
                      // EXTERNAL LINK SAFEGUARD: skip ANY routing logic
                      // wa.me must NEVER pass through SPA router
                      e.stopPropagation();
                      trackWhatsAppClick({ phone: effectivePhone, template: tplId, action: 'direct_link' });
                    }}
                    className="text-xs text-brand-600 hover:underline break-all font-mono"
                    data-testid="wa-direct-link"
                  >
                    {previewURL}
                  </a>
                </div>
                <p className="text-xs text-green-700 mt-1">
                  💡 انقر على الرابط أعلاه أو انسخه — يفتح في تبويب جديد
                </p>
              </div>
            )}

            {/* Network notice */}
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <div>
                إذا لم يفتح WhatsApp بسبب قيود الشبكة، استخدم زر <strong>"نسخ الرسالة"</strong> ثم الصقها في WhatsApp يدوياً.
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t flex-wrap">
              <Button variant="outline" onClick={() => setOpen(false)}>إلغاء</Button>
              <Button
                variant="outline"
                onClick={handleCopyMessage}
                className="bg-brand-50 hover:bg-brand-100 border-brand-300 text-brand-700"
                data-testid="wa-copy-btn"
              >
                <Copy className="h-4 w-4 ml-2" /> نسخ الرسالة
              </Button>
              {phoneStatus.valid ? (
                <a
                  href={previewURL}
                  target="_blank"
                  rel="noopener noreferrer external"
                  referrerPolicy="no-referrer"
                  onClick={(e) => {
                    // ============================================
                    // EXTERNAL LINK SAFEGUARD — wa.me ONLY
                    // ============================================
                    // 1) Stop React/SPA event propagation
                    e.stopPropagation();
                    // 2) Skip ANY router interception by explicitly
                    //    opening via window.open in a new tab.
                    //    The default <a target="_blank"> still works
                    //    as a fallback if window.open is blocked.
                    if (previewURL && previewURL.includes('wa.me')) {
                      e.preventDefault();
                      window.open(previewURL, '_blank', 'noopener,noreferrer');
                    }
                    trackWhatsAppClick({ phone: effectivePhone, template: tplId, clientName });
                    setOpen(false);
                  }}
                  className="inline-flex items-center bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                  data-testid="wa-send-btn"
                >
                  <MessageCircle className="h-4 w-4 ml-2" /> فتح WhatsApp
                </a>
              ) : (
                <Button disabled className="bg-green-600 text-white opacity-50">
                  <MessageCircle className="h-4 w-4 ml-2" /> فتح WhatsApp
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WhatsAppButton;
